import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import { Marquee } from "../components/Marquee";
import { Newsletter } from "../components/Newsletter";
import { Reveal } from "../components/Reveal";
import { COLLECTIONS } from "../data/products";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay, className = "" }) => (
  <div className="overflow-hidden">
    <motion.div
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  </div>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-[88vh] items-center overflow-hidden bg-cocoa" data-testid="hero-section">
      <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
        <img
          src="/images/hero.jpg?v=2"
          alt="A lit Zelvoria candle in an amber glass jar beside cream roses on a dark walnut table"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1d150e]/95 via-cocoa/60 to-cocoa/20" />
      </motion.div>
      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 md:px-10">
        <div className="max-w-2xl py-24">
          <MaskedLine delay={0.25}>
            <span className="text-[11px] uppercase tracking-[0.45em] text-gold md:text-xs">Welcome To</span>
          </MaskedLine>
          <MaskedLine delay={0.4}>
            <h1 className="mt-3 font-serif text-6xl font-light tracking-[0.06em] text-ivory md:text-8xl">ZELVORIA</h1>
          </MaskedLine>
          <MaskedLine delay={0.55}>
            <p className="mt-5 font-serif text-2xl font-light italic text-beige md:text-3xl">
              Timeless Elegance. In Every Detail.
            </p>
          </MaskedLine>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1, delay: 0.95, ease: EASE }}
            className="my-8 h-px w-28 origin-left bg-gold"
          />
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9, ease: EASE }}
            className="max-w-md text-sm font-light leading-relaxed text-cream/80 md:text-base"
          >
            A master brand for your everyday luxuries — from candles to jewellery, and beyond.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9, ease: EASE }}
            className="mt-10"
          >
            <Link
              to="/candles"
              data-testid="hero-explore-button"
              className="group inline-flex items-center gap-3 border border-cream/50 px-9 py-4 text-[11px] uppercase tracking-[0.3em] text-ivory transition-colors duration-500 hover:border-gold hover:bg-gold"
            >
              Explore Our Collections
              <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.7, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <div className="h-12 w-px bg-gradient-to-b from-transparent via-cream/60 to-transparent" />
      </motion.div>
    </section>
  );
};

const CandlesFeature = () => (
  <section className="bg-parchment" data-testid="candles-feature">
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 py-24 md:px-10 md:py-32 lg:grid-cols-2 lg:gap-20">
      <div>
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.35em] text-gold">Our First Collection</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 font-serif text-4xl font-light leading-tight text-ink md:text-5xl lg:text-6xl">
            Zelvoria Candles
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-4 font-serif text-xl italic text-gold md:text-2xl">
            More than just a candle. It's a feeling.
          </p>
        </Reveal>
        <Reveal delay={0.26}>
          <p className="mt-6 max-w-md text-sm font-light leading-relaxed text-taupe md:text-base">
            Handcrafted with premium waxes and exquisite fragrances, our candles bring warmth, peace and luxury to
            your space.
          </p>
        </Reveal>
        <Reveal delay={0.32}>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            {["Lavender", "Vanilla", "Sandalwood", "Oud"].map((f, i) => (
              <span key={f} className="flex items-center gap-4">
                <span className="text-[10px] uppercase tracking-[0.25em] text-taupe">{f}</span>
                {i < 3 && <span className="h-1 w-1 rotate-45 bg-gold/60" />}
              </span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.4}>
          <Link
            to="/candles"
            data-testid="feature-shop-candles-button"
            className="group mt-10 inline-flex items-center gap-3 bg-cocoa px-10 py-4 text-[11px] uppercase tracking-[0.3em] text-ivory transition-colors duration-500 hover:bg-gold"
          >
            Shop Candles
            <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <Reveal delay={0.15} className="relative">
        <div className="relative">
          <div className="absolute -right-4 -top-4 h-full w-full border border-gold/40 md:-right-6 md:-top-6" />
          <div className="relative aspect-[4/5] overflow-hidden">
            <img
              src="/images/collection.jpg?v=2"
              alt="The Zelvoria candle collection — glass jars with ivory labels and gold bands on travertine stone"
              className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out hover:scale-105"
            />
            <div
              className="pointer-events-none absolute inset-0"
              style={{ background: "radial-gradient(ellipse at 50% 35%, rgba(255,240,210,0.16), transparent 60%)" }}
            />
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const ComingSoonCards = () => (
  <section className="bg-ivory" data-testid="coming-soon-collections">
    <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-28">
      <Reveal className="mb-14 text-center">
        <p className="text-[11px] uppercase tracking-[0.35em] text-gold">The World of Zelvoria</p>
        <h2 className="mt-4 font-serif text-4xl font-light text-ink md:text-5xl">Collections on the Horizon</h2>
      </Reveal>
      <div className="grid gap-6 md:grid-cols-2">
        {COLLECTIONS.filter((c) => c.status === "coming-soon").map((c, i) => (
          <Reveal key={c.slug} delay={i * 0.12}>
            <Link
              to={c.path}
              data-testid={`collection-card-${c.slug}`}
              className="group relative block h-[60vh] min-h-[420px] overflow-hidden"
            >
              <img
                src={c.image}
                alt={`${c.name} editorial`}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-cocoa/85 via-cocoa/25 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-8 md:p-12">
                <p className="text-[10px] uppercase tracking-[0.35em] text-gold">Coming Soon</p>
                <h3 className="mt-3 font-serif text-3xl font-light text-ivory md:text-4xl">{c.name}</h3>
                <p className="mt-2 text-sm font-light text-cream/75">{c.tagline}</p>
                <span className="mt-6 inline-flex items-center gap-3 border border-cream/40 px-7 py-3 text-[10px] uppercase tracking-[0.28em] text-ivory transition-colors duration-500 group-hover:border-gold group-hover:bg-gold">
                  Explore <ArrowRight size={13} />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CHAPTERS = [
  {
    n: "01",
    title: "The Craft",
    desc: "Hand-poured with slow-burning botanical wax and bespoke fragrance compositions inspired by Grasse and Mysore.",
  },
  {
    n: "02",
    title: "The Essence",
    desc: "Designed to turn everyday spaces into sanctuaries of calm, warmth and quiet introspection.",
  },
  {
    n: "03",
    title: "The Legacy",
    desc: "Rooted in modern Indian luxury — one unmistakable Zelvoria identity across candles, jewellery, fashion and beyond.",
  },
];

const StorySection = () => (
  <section className="bg-parchment" data-testid="story-section">
    <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
      <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
        <Reveal className="relative order-2 lg:order-1">
          <div className="relative">
            <div className="absolute -left-4 -top-4 h-full w-full border border-gold/40 md:-left-6 md:-top-6" />
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src="/images/story.jpg?v=2"
                alt="A cream Zelvoria interior with lit candles, garden roses and linen-bound books"
                className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out hover:scale-105"
              />
            </div>
          </div>
        </Reveal>
        <div className="order-1 lg:order-2">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.35em] text-gold">The Zelvoria Story</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-serif text-4xl font-light leading-tight text-ink md:text-5xl">
              More Than Just Products.
              <br />
              <span className="italic text-gold">It's a Lifestyle.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-lg text-sm font-light leading-relaxed text-taupe md:text-base">
              Zelvoria is a master brand, born from a vision to bring timeless elegance and refined luxury into your
              everyday life.
            </p>
            <p className="mt-4 max-w-lg text-sm font-light leading-relaxed text-taupe md:text-base">
              What began with candles is just the beginning — soon, we'll be curating exquisite jewellery, stylish
              clothing and more, all under one name: Zelvoria.
            </p>
            <p className="mt-6 font-serif text-lg italic text-ink md:text-xl">
              Different categories. One unmistakable Zelvoria identity.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <Link
              to="/about"
              data-testid="story-our-story-link"
              className="group mt-8 inline-flex items-center gap-4 text-[11px] uppercase tracking-[0.3em] text-gold"
            >
              Our Story
              <span className="h-px w-10 bg-gold transition-all duration-500 group-hover:w-16" />
            </Link>
          </Reveal>
        </div>
      </div>
      <div className="mt-24 grid gap-10 border-t border-beige pt-14 md:grid-cols-3">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.1}>
            <p className="font-serif text-3xl font-light text-gold/80">{c.n}</p>
            <h3 className="mt-3 font-serif text-2xl text-ink">{c.title}</h3>
            <p className="mt-3 max-w-xs text-sm font-light leading-relaxed text-taupe">{c.desc}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="ZELVORIA — Timeless Elegance. In Every Detail."
        siteName="ZELVORIA"
        description="A master brand for your everyday luxuries — from hand-poured candles to jewellery, fashion and beyond."
        image="/images/hero.jpg"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "ZELVORIA",
          slogan: "Timeless Elegance. In Every Detail.",
          description:
            "A premium Indian luxury lifestyle master brand. Launching with hand-poured scented candles; jewellery and fashion coming soon.",
        }}
      />
      <Hero />
      <Marquee />
      <CandlesFeature />
      <ComingSoonCards />
      <StorySection />
      <Newsletter />
    </div>
  );
}
